import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useBranch } from "@/contexts/BranchContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { AlertCircle, Sigma, Link as LinkIcon, CalendarDays, Save } from "lucide-react";
import { Link } from "react-router-dom";

const DAY_TYPES = [
  { key: "regular_work_days", label: "REGULAR WORK DAYS" },
  { key: "regular_holidays", label: "REGULAR HOLIDAYS" },
  { key: "special_holidays", label: "SPECIAL HOLIDAYS" },
];

const CONDITIONS = [
  { key: "worked", label: "Worked", default: "100%" },
  { key: "rest_day", label: "Rest Day", default: "0%" },
  { key: "worked_on_rest_day", label: "Worked on Rest Day", default: "130%" },
  { key: "worked_under_time", label: "Worked Under-time", default: "100%" },
  { key: "worked_under_time_on_rest_day", label: "Worked Under-time on Rest Day", default: "130%" },
  { key: "official_leave_with_pay", label: "Official Leave With Pay", default: "100%" },
  { key: "overtime_pay", label: "Overtime Pay", default: "125%" },
  { key: "late", label: "Late", default: "Deduct", options: ["Deduct", "No Deduct"] },
];

export default function SalaryFormulaPage() {
  const { selectedBranch } = useBranch();

  return (
    <>
      <div className="mb-6">
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground tracking-tight">Employee Salary Formula</h1>
        <p className="text-brand-inkMuted mt-1">
          Define how each income column is computed for every salary condition.
          {selectedBranch && <> Currently editing <span className="font-semibold text-foreground">{selectedBranch.name}</span>.</>}
        </p>
      </div>

      {!selectedBranch ? (
        <NoBranchCard />
      ) : (
        <Tabs defaultValue="regular_work_days" className="w-full">
          <TabsList className="bg-brand-purpleLight rounded-full p-1 h-auto flex-wrap">
            {DAY_TYPES.map((dt) => (
              <TabsTrigger
                key={dt.key}
                value={dt.key}
                className="rounded-full px-5 py-2 data-[state=active]:bg-white data-[state=active]:text-brand-purpleHover"
                data-testid={`formula-tab-${dt.key.replace(/_/g, "-")}`}
              >
                <CalendarDays className="w-4 h-4 mr-1.5" />
                {dt.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {DAY_TYPES.map((dt) => (
            <TabsContent key={dt.key} value={dt.key} className="mt-6">
              <FormulaPanel branch={selectedBranch} dayType={dt.key} />
            </TabsContent>
          ))}
        </Tabs>
      )}
    </>
  );
}

function FormulaPanel({ branch, dayType }) {
  const [labels, setLabels] = useState([]);
  const [cells, setCells] = useState({}); // committed values
  const [draft, setDraft] = useState({}); // pending edits {label_id: {condition: value}}
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lbl, cs] = await Promise.all([
        api.get("/salary-labels", { params: { branch_id: branch.id, kind: "income" } }),
        api.get("/salary-formula", { params: { branch_id: branch.id, day_type: dayType } }),
      ]);
      setLabels(lbl.data || []);
      const map = {};
      (cs.data || []).forEach((c) => {
        map[c.label_id] = map[c.label_id] || {};
        map[c.label_id][c.condition] = c.value;
      });
      setCells(map);
      setDraft({});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [branch.id, dayType]);

  useEffect(() => { load(); }, [load]);

  const stageCell = (labelId, condition, value) => {
    setDraft((prev) => {
      const next = { ...prev };
      next[labelId] = { ...(next[labelId] || {}), [condition]: value };
      return next;
    });
  };

  // Merge committed + draft for rendering
  const merged = useMemo(() => {
    const out = { ...cells };
    for (const lid of Object.keys(draft)) {
      out[lid] = { ...(cells[lid] || {}), ...draft[lid] };
    }
    return out;
  }, [cells, draft]);

  const pendingCount = useMemo(
    () => Object.values(draft).reduce((s, x) => s + Object.keys(x).length, 0),
    [draft]
  );
  const hasChanges = pendingCount > 0;

  const saveAll = async () => {
    if (!hasChanges) return;
    setSaving(true);
    let ok = 0, failed = 0;
    try {
      for (const labelId of Object.keys(draft)) {
        for (const condition of Object.keys(draft[labelId])) {
          const value = draft[labelId][condition];
          try {
            await api.put("/salary-formula/cell", {
              branch_id: branch.id,
              label_id: labelId,
              condition,
              value,
              day_type: dayType,
            });
            ok += 1;
          } catch (e) {
            failed += 1;
            toast.error(formatApiError(e));
          }
        }
      }
      if (ok > 0) toast.success(`Saved ${ok} cell${ok === 1 ? "" : "s"}${failed ? `, ${failed} failed` : ""}`);
      load();
    } finally {
      setSaving(false);
    }
  };

  const discard = () => setDraft({});

  if (labels.length === 0) return <EmptyState loading={loading} />;

  return (
    <div>
      <div className="flex items-center justify-end gap-2 mb-4">
        {hasChanges && (
          <Button onClick={discard} variant="outline"
            className="rounded-2xl border-brand-purple/30"
            data-testid="formula-discard-btn">
            Discard
          </Button>
        )}
        <Button onClick={saveAll} disabled={!hasChanges || saving}
          className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white disabled:opacity-50"
          data-testid="formula-save-btn">
          <Save className="w-4 h-4 mr-1" />
          {saving ? "Saving..." : hasChanges ? `Save (${pendingCount})` : "Save"}
        </Button>
      </div>
      <FormulaTable labels={labels} cells={merged} loading={loading} onStageCell={stageCell} />
    </div>
  );
}

function FormulaTable({ labels, cells, loading, onStageCell }) {
  return (
    <div className="bg-white rounded-3xl border border-brand-purple/10 shadow-sm overflow-hidden" data-testid="formula-table">
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="bg-brand-purpleLight/60">
              <th className="text-left text-sm font-heading font-semibold text-foreground px-5 py-3 w-64">Salary Condition</th>
              {labels.map((l) => (
                <th key={l.id} className="text-center text-sm font-heading font-semibold text-foreground px-5 py-3 whitespace-nowrap" data-testid={`column-${l.id}`}>
                  {l.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={labels.length + 1} className="text-center text-brand-inkMuted py-8">Loading...</td></tr>
            ) : CONDITIONS.map((cond, i) => (
              <tr key={cond.key} className={i % 2 ? "bg-white" : "bg-brand-cream/40"}>
                <td className="px-5 py-3 font-body text-foreground">{cond.label}</td>
                {labels.map((l) => (
                  <td key={l.id} className="px-5 py-3" data-testid={`cell-${l.id}-${cond.key}`}>
                    <FormulaCell
                      value={cells[l.id]?.[cond.key] ?? ""}
                      condition={cond}
                      onSave={(v) => onStageCell(l.id, cond.key, v)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FormulaCell({ value, condition, onSave }) {
  const [local, setLocal] = useState(value);
  const initial = useRef(value);
  // sync if upstream changes
  useEffect(() => { setLocal(value); initial.current = value; }, [value]);

  if (condition.options) {
    return (
      <select
        value={local || ""}
        onChange={(e) => { setLocal(e.target.value); onSave(e.target.value); }}
        className="w-full h-10 rounded-2xl border border-brand-purple/20 bg-white px-3 font-body text-foreground text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple"
        data-testid="formula-select"
      >
        <option value="">— select —</option>
        {condition.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }

  const formatAsPercent = (raw) => {
    if (raw == null) return "";
    const trimmed = String(raw).trim();
    if (trimmed === "") return "";
    // Strip all % symbols, keep digits/dot/minus
    const cleaned = trimmed.replace(/%/g, "").trim();
    if (cleaned === "" || cleaned === "-" || cleaned === ".") return trimmed;
    // If purely numeric (optionally negative / decimal), append %
    if (/^-?\d+(\.\d+)?$/.test(cleaned)) return `${cleaned}%`;
    return trimmed; // Non-numeric (e.g. custom text) — leave as-is
  };

  const commit = () => {
    const formatted = formatAsPercent(local);
    if (formatted !== local) setLocal(formatted);
    if (formatted !== initial.current) onSave(formatted);
  };

  return (
    <Input
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      className="rounded-2xl h-10 text-center border-brand-purple/20 font-body"
      placeholder="e.g. 100%"
      data-testid="formula-input"
    />
  );
}

function EmptyState({ loading }) {
  if (loading) return <div className="text-brand-inkMuted font-heading">Loading...</div>;
  return (
    <div className="bg-white rounded-3xl border border-brand-purple/10 p-10 text-center max-w-2xl" data-testid="formula-empty">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-brand-purpleLight flex items-center justify-center mb-3">
        <Sigma className="w-7 h-7 text-brand-purple" />
      </div>
      <p className="font-heading text-lg font-semibold text-foreground">No income columns yet</p>
      <p className="text-brand-inkMuted mt-1">
        Add income labels in the <strong>Salary Module</strong> to start defining formulas here.
      </p>
      <Link to="/employees/salary-module" className="inline-flex items-center gap-1.5 mt-4 rounded-full bg-brand-purple hover:bg-brand-purpleHover text-white px-5 py-2 text-sm font-semibold">
        <LinkIcon className="w-4 h-4" /> Go to Salary Module
      </Link>
    </div>
  );
}

function NoBranchCard() {
  return (
    <div className="bg-white rounded-3xl border border-brand-purple/10 p-10 text-center max-w-xl" data-testid="no-branch-picked">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-[#FFF3E0] flex items-center justify-center mb-3">
        <AlertCircle className="w-7 h-7 text-[#E65100]" />
      </div>
      <p className="font-heading text-lg font-semibold text-foreground">Pick a branch first</p>
      <p className="text-brand-inkMuted mt-1">Formulas are scoped per branch. Use the dropdown in the header.</p>
    </div>
  );
}
